import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

const Cursor = styled.div`
  position: fixed;
  width: 50px;
  height: 50px;
  border: 2px solid #fff;
  border-radius: 50%;
  pointer-events: none;
  transform: translate(-50%, -50%);
  mix-blend-mode: difference;
  z-index: 9999;
  @media (hover: none) {
    display: none;
  }
`;

const Follower = styled.div`
  position: fixed;
  width: 30px;
  height: 30px;
  background-color: #fff;
  border-radius: 50%;
  pointer-events: none;
  transform: translate(-50%, -50%);
  mix-blend-mode: difference;
  z-index: 9998;
  transition: width 0.2s ease, height 0.2s ease;
  @media (hover: none) {
    display: none;
  }
`;

function CustomCursor() {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [followerPosition, setFollowerPosition] = useState({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);
  const location = useLocation();

  // Holds the latest target position for the follower.
  const targetRef = useRef({ x: 0, y: 0 });
  // Tracks the current hovering state for use in the animation loop.
  const hoveringRef = useRef(hovering);

  const handleMouseMove = (e) => {
    const newPos = { x: e.clientX, y: e.clientY };
    setCursorPosition(newPos);
    targetRef.current = newPos;
  };

  const initializeCursorHandlers = () => {
    const handleMouseEnter = () => setHovering(true);
    const handleMouseLeave = () => setHovering(false);

    const setupHoverListeners = () => {
      const clickableElements = document.querySelectorAll(
        'a, button, input[type="button"], input[type="submit"], [role="button"], .hoverable, [data-hoverable="true"], .experiment-item, [class*="ExperimentStyled"]'
      );
      clickableElements.forEach((el) => {
        el.addEventListener("mouseenter", handleMouseEnter);
        el.addEventListener("mouseleave", handleMouseLeave);
      });
      return clickableElements;
    };

    let elements = setupHoverListeners();
    const observer = new MutationObserver(() => {
      elements.forEach((el) => {
        el.removeEventListener("mouseenter", handleMouseEnter);
        el.removeEventListener("mouseleave", handleMouseLeave);
      });
      elements = setupHoverListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      elements.forEach((el) => {
        el.removeEventListener("mouseenter", handleMouseEnter);
        el.removeEventListener("mouseleave", handleMouseLeave);
      });
      observer.disconnect();
    };
  };

  useEffect(() => {
    const cleanup = initializeCursorHandlers();
    setHovering(false);
    return cleanup;
  }, [location]);

  // Synchronize the hovering ref with the state.
  useEffect(() => {
    hoveringRef.current = hovering;
  }, [hovering]);

  // Animation loop for the follower with dynamic lag.
  useEffect(() => {
    let animationFrameId;
    const updateFollower = () => {
      // Use a faster smoothing factor when hovering.
      const smoothingFactor = hoveringRef.current ? 0.3 : 0.1;
      setFollowerPosition((prev) => ({
        x: prev.x + (targetRef.current.x - prev.x) * smoothingFactor,
        y: prev.y + (targetRef.current.y - prev.y) * smoothingFactor,
      }));
      animationFrameId = requestAnimationFrame(updateFollower);
    };
    animationFrameId = requestAnimationFrame(updateFollower);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  return (
    <>
      <Cursor style={{ left: `${cursorPosition.x}px`, top: `${cursorPosition.y}px` }} />
      <Follower
        style={{
          left: `${followerPosition.x}px`,
          top: `${followerPosition.y}px`,
          width: hovering ? "50px" : "30px",
          height: hovering ? "50px" : "30px",
        }}
      />
    </>
  );
}

export default CustomCursor;
